import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronRight, Database, Loader2, Plus, Server, Trash2, X, Zap } from "lucide-react";
import {
  BusinessDataConnection,
  CreateConnectionInput,
  McpTestResult,
  testBusinessDataConnection,
  testMcpConnection,
  TestConnectionResult,
  useBusinessDataConnections,
} from "@/hooks/useBusinessDataConnections";

type Step = "type" | "describe" | "endpoint" | "test" | "confirm";
type ConnType = "url_feed" | "rest_api" | "rest_action" | "mcp_remote";
type AuthType = "none" | "bearer" | "header" | "basic" | "query";

// rest_api (read-only query) and rest_action (write/action) share the same
// endpoint + parameters + body configuration. Only their intent differs, and
// the AI-side gating (rest_action is approval-gated like booking/payment).
const isCallableType = (t: ConnType) => t === "rest_api" || t === "rest_action";
// mcp_remote: a remote MCP server. One endpoint (the server URL) exposing many
// tools we discover, so it has no per-call args/body, but it IS gated
// (action_mode/success_message) like rest_action.
const isMcp = (t: ConnType) => t === "mcp_remote";
// Types whose write/action tool calls are approval-gated.
const isGatedType = (t: ConnType) => t === "rest_action" || t === "mcp_remote";

interface ArgField {
  name: string;
  type: "STRING" | "INTEGER" | "NUMBER" | "BOOLEAN";
  description: string;
  required: boolean;
}

interface FormState {
  connection_type: ConnType;
  name: string;
  description: string;
  endpoint_url: string;
  http_method: "GET" | "POST";
  auth_type: AuthType;
  auth_creds: Record<string, string>;
  refresh_interval_seconds: number;
  // rest_api: arg schema fields the AI will fill when calling the tool.
  args: ArgField[];
  // rest_api POST: optional JSON body template (kept as a string the user
  // pastes; we parse it at save time).
  request_body_template_text: string;
  // rest_action only: how the AI may run it, and what to tell the customer
  // once it has run.
  action_mode: "auto" | "ask" | "off";
  success_message: string;
}

const EMPTY_FORM: FormState = {
  connection_type: "url_feed",
  name: "",
  description: "",
  endpoint_url: "",
  http_method: "GET",
  auth_type: "none",
  auth_creds: {},
  refresh_interval_seconds: 300,
  args: [],
  request_body_template_text: "",
  action_mode: "ask",
  success_message: "",
};

function fieldsToSchema(fields: ArgField[]): unknown {
  if (fields.length === 0) return { type: "OBJECT", properties: {}, required: [] };
  const properties: Record<string, { type: string; description: string }> = {};
  const required: string[] = [];
  for (const f of fields) {
    if (!f.name.trim()) continue;
    properties[f.name.trim()] = { type: f.type, description: f.description };
    if (f.required) required.push(f.name.trim());
  }
  return { type: "OBJECT", properties, required };
}

function ResultPreview({ result }: { result: TestConnectionResult }) {
  const [showRaw, setShowRaw] = useState(false);
  if (!result.ok) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          <div className="font-semibold">Connection failed</div>
          <div className="text-xs mt-1">{result.error}</div>
        </AlertDescription>
      </Alert>
    );
  }
  const data = result.data;
  const summary = (() => {
    if (Array.isArray(data)) return `Array with ${data.length} item${data.length === 1 ? "" : "s"}`;
    if (data && typeof data === "object") return `Object with ${Object.keys(data as object).length} field${Object.keys(data as object).length === 1 ? "" : "s"}`;
    return typeof data;
  })();
  const preview = Array.isArray(data) ? data.slice(0, 3) : data;
  return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <Check className="w-4 h-4 text-green-600" />
        <span className="font-semibold text-green-900">Success</span>
        <span className="text-xs text-green-700">
          {summary} · {result.bytes ? `${(result.bytes / 1024).toFixed(1)} KB` : "-"} · {result.ms}ms
        </span>
      </div>
      {result.bytes && result.bytes > 50 * 1024 && (
        <p className="text-xs text-amber-700">
          ⚠️ Response is large ({(result.bytes / 1024).toFixed(0)} KB). The AI will only see the first 6 KB per turn — consider exposing a summary endpoint or paginating.
        </p>
      )}
      <button
        type="button"
        onClick={() => setShowRaw((s) => !s)}
        className="flex items-center gap-1 text-xs text-green-800 hover:underline"
      >
        {showRaw ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {showRaw ? "Hide" : "Show"} raw response
      </button>
      {showRaw && (
        <pre className="text-[10px] bg-white border rounded p-2 max-h-64 overflow-auto font-mono">
{JSON.stringify(preview, null, 2)}
        </pre>
      )}
    </div>
  );
}

function McpResultPreview({ result }: { result: McpTestResult }) {
  if (!result.ok) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          <div className="font-semibold">Couldn't connect to the MCP server</div>
          <div className="text-xs mt-1">{result.error}</div>
        </AlertDescription>
      </Alert>
    );
  }
  const tools = result.tools ?? [];
  return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <Check className="w-4 h-4 text-green-600" />
        <span className="font-semibold text-green-900">Connected</span>
        <span className="text-xs text-green-700">
          {tools.length} tool{tools.length === 1 ? "" : "s"} discovered
        </span>
      </div>
      {tools.length === 0 ? (
        <p className="text-xs text-amber-700">The server exposed no tools. Check that it advertises tools via <code>tools/list</code>.</p>
      ) : (
        <ul className="space-y-1 max-h-64 overflow-auto">
          {tools.map((t) => (
            <li key={t.name} className="text-xs bg-white border rounded p-2">
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium">{t.name}</span>
                <Badge variant="outline" className="text-[10px]">
                  {t.annotations?.readOnlyHint ? "lookup" : "action — needs approval"}
                </Badge>
              </div>
              {t.description && <div className="text-muted-foreground mt-0.5">{t.description}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function BusinessDataConnectionDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: BusinessDataConnection | null;
}) {
  const { create, update } = useBusinessDataConnections();
  const [step, setStep] = useState<Step>("type");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [mcpTestResult, setMcpTestResult] = useState<McpTestResult | null>(null);

  // Reset / hydrate when (re)opened.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      // Hydrate from the existing connection. Credentials are redacted on the
      // wire, so we start with an empty creds object. The user can leave them
      // alone (we'll send no auth_type, so the backend keeps the existing
      // credentials) or re-enter them to change.
      const props = (editing.argument_schema as any)?.properties ?? {};
      const req = new Set(((editing.argument_schema as any)?.required ?? []) as string[]);
      setForm({
        connection_type: editing.connection_type as ConnType,
        name: editing.name,
        description: editing.description,
        endpoint_url: editing.endpoint_url,
        http_method: editing.http_method,
        auth_type: editing.auth_type as AuthType,
        auth_creds: {},
        refresh_interval_seconds: editing.refresh_interval_seconds,
        args: Object.entries(props).map(([name, schema]: [string, any]) => ({
          name,
          type: (schema?.type ?? "STRING") as ArgField["type"],
          description: schema?.description ?? "",
          required: req.has(name),
        })),
        request_body_template_text: editing.request_body_template ? JSON.stringify(editing.request_body_template, null, 2) : "",
        action_mode: (editing.action_mode as FormState["action_mode"]) ?? "ask",
        success_message: editing.success_message ?? "",
      });
      setStep("describe");
    } else {
      setForm(EMPTY_FORM);
      setStep("type");
    }
    setTestResult(null);
    setMcpTestResult(null);
  }, [open, editing]);

  const stepIndex = useMemo(() => ["type", "describe", "endpoint", "test", "confirm"].indexOf(step), [step]);
  const canBack = stepIndex > 0;

  const goBack = () => {
    if (stepIndex === 0) return;
    const prev = ["type", "describe", "endpoint", "test", "confirm"][stepIndex - 1] as Step;
    setStep(prev);
  };

  const goNext = () => {
    if (step === "type") setStep("describe");
    else if (step === "describe") setStep("endpoint");
    else if (step === "endpoint") setStep("test");
    else if (step === "test") setStep("confirm");
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setMcpTestResult(null);

    // MCP: handshake the server and list its tools.
    if (isMcp(form.connection_type)) {
      const result = await testMcpConnection({
        endpoint_url: form.endpoint_url,
        auth_type: form.auth_type,
        auth_credentials: form.auth_type === "none" ? null : form.auth_creds,
      });
      setTesting(false);
      setMcpTestResult(result);
      return;
    }

    let body: unknown = null;
    if (isCallableType(form.connection_type) && form.http_method === "POST" && form.request_body_template_text.trim()) {
      try { body = JSON.parse(form.request_body_template_text); }
      catch { setTesting(false); setTestResult({ ok: false, error: "Request body is not valid JSON" }); return; }
    }
    const result = await testBusinessDataConnection({
      endpoint_url: form.endpoint_url,
      http_method: form.http_method,
      auth_type: form.auth_type,
      auth_credentials: form.auth_type === "none" ? null : form.auth_creds,
      request_body_template: body,
      args: {},
    });
    setTesting(false);
    setTestResult(result);
  };

  const handleSave = async () => {
    let body: unknown = null;
    if (isCallableType(form.connection_type) && form.http_method === "POST" && form.request_body_template_text.trim()) {
      try { body = JSON.parse(form.request_body_template_text); }
      catch { return; }
    }
    const payload: CreateConnectionInput = {
      connection_type: form.connection_type,
      name: form.name,
      description: form.description,
      endpoint_url: form.endpoint_url,
      http_method: form.http_method,
      auth_type: form.auth_type,
      // On edit, only send auth_credentials if the user re-entered them.
      // The CRUD function requires sending auth_type + auth_credentials
      // together, so when editing with empty creds, we omit auth_type too.
      auth_credentials: form.auth_type === "none" ? null : form.auth_creds,
      request_body_template: body,
      refresh_interval_seconds: form.refresh_interval_seconds,
      // MCP has no single argument_schema (each discovered tool self-describes).
      argument_schema: isCallableType(form.connection_type) ? fieldsToSchema(form.args) : null,
      ...(isGatedType(form.connection_type) && {
        action_mode: form.action_mode,
        success_message: form.success_message.trim() || null,
      }),
    };

    if (editing) {
      const hasNewCreds = form.auth_type !== "none" && Object.keys(form.auth_creds).length > 0;
      const patch: any = {
        id: editing.id,
        name: payload.name,
        description: payload.description,
        endpoint_url: payload.endpoint_url,
        http_method: payload.http_method,
        request_body_template: payload.request_body_template,
        refresh_interval_seconds: payload.refresh_interval_seconds,
        argument_schema: payload.argument_schema,
      };
      if (isGatedType(form.connection_type)) {
        patch.action_mode = form.action_mode;
        patch.success_message = form.success_message.trim() || null;
      }
      if (hasNewCreds || form.auth_type === "none") {
        patch.auth_type = payload.auth_type;
        patch.auth_credentials = payload.auth_credentials;
      }
      update.mutate(patch, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  };

  const isSaving = create.isPending || update.isPending;

  const nextDisabled = (() => {
    if (step === "describe") return !form.name.trim() || !form.description.trim();
    if (step === "endpoint") {
      if (!form.endpoint_url.trim()) return true;
      if (form.auth_type === "bearer" && !form.auth_creds.token) return true;
      if (form.auth_type === "header" && (!form.auth_creds.header || !form.auth_creds.value)) return true;
      if (form.auth_type === "basic" && !form.auth_creds.user) return true;
      if (form.auth_type === "query" && (!form.auth_creds.param || !form.auth_creds.value)) return true;
      return false;
    }
    if (step === "test") {
      // Actions perform a REAL side effect when tested, so we don't force a
      // successful test before saving. The owner can skip it.
      if (form.connection_type === "rest_action") return false;
      // MCP tools/list is a safe read. Require it to succeed so the owner sees
      // the discovered tools before saving.
      if (isMcp(form.connection_type)) return !mcpTestResult?.ok;
      return !testResult?.ok;
    }
    return false;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit data source" : "Connect a data source"}</DialogTitle>
          <DialogDescription>
            Step {stepIndex + 1} of 5 — {
              step === "type" ? "choose how the AI gets your data" :
              step === "describe" ? "name and describe this data source" :
              step === "endpoint" ? "configure the endpoint and authentication" :
              step === "test" ? "verify the connection works" :
              "review and save"
            }
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: type picker */}
        {step === "type" && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, connection_type: "url_feed", http_method: "GET" }))}
              className={`w-full text-left p-4 border rounded-xl transition-all ${form.connection_type === "url_feed" ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Database className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    Live data feed (URL)
                    {form.connection_type === "url_feed" && <Check className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    A URL that returns JSON. We fetch it on a schedule and the AI sees the data on every customer message. Best for property listings, product catalogs, class schedules — small datasets that change predictably.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, connection_type: "rest_api" }))}
              className={`w-full text-left p-4 border rounded-xl transition-all ${form.connection_type === "rest_api" ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    On-demand API (advanced)
                    {form.connection_type === "rest_api" && <Check className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    An endpoint with parameters. The AI calls it only when relevant — e.g. when a customer asks "is unit 4B still available?". Best for real-time inventory checks, large datasets, or one-item-at-a-time lookups.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, connection_type: "rest_action", http_method: "POST" }))}
              className={`w-full text-left p-4 border rounded-xl transition-all ${form.connection_type === "rest_action" ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    Action (lets the AI do something)
                    {form.connection_type === "rest_action" && <Check className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    An endpoint that <span className="font-medium">performs a real action</span> in your system — place an order, submit a request, update a record. The AI calls it when a customer wants it done. By default each call waits for your approval in the Action Center before it runs.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, connection_type: "mcp_remote", http_method: "POST" }))}
              className={`w-full text-left p-4 border rounded-xl transition-all ${form.connection_type === "mcp_remote" ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                  <Server className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    MCP Server (advanced)
                    {form.connection_type === "mcp_remote" && <Check className="w-4 h-4 text-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Bring your own Model Context Protocol server. We discover all its tools automatically — lookups run on demand, and actions wait for your approval by default. Best if you already run an MCP server for your systems.
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Step 2: describe */}
        {step === "describe" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="data-name">Name</Label>
              <Input
                id="data-name"
                placeholder="e.g. Property listings"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={80}
              />
              <p className="text-xs text-muted-foreground">Used internally and as part of the AI tool name. Short and descriptive.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="data-desc">Description (the AI reads this)</Label>
              <Textarea
                id="data-desc"
                placeholder={form.connection_type === "url_feed"
                  ? "e.g. JSON array of properties currently for rent. Each item has: id, address, monthly_rent, bedrooms, bathrooms, status (available/pending/rented), image_url, listing_url."
                  : "e.g. Look up availability for a specific listing. Pass listing_id and a target date. Returns: status (available/booked), next_available_date, viewing_slots[]."
                }
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                maxLength={2000}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                Be specific — the AI uses this to decide when to use this data and how to interpret it. Mention the shape, the field names, and what each one means.
              </p>
            </div>
          </div>
        )}

        {/* Step 3: endpoint + auth */}
        {step === "endpoint" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="data-url">{isMcp(form.connection_type) ? "MCP server URL" : "Endpoint URL"}</Label>
              <Input
                id="data-url"
                placeholder={isMcp(form.connection_type) ? "https://your-mcp-server.com/mcp" : "https://your-business.com/api/listings.json"}
                value={form.endpoint_url}
                onChange={(e) => setForm((f) => ({ ...f, endpoint_url: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                {isMcp(form.connection_type)
                  ? "Must be HTTPS. The Streamable HTTP endpoint of your MCP server — we'll handshake it and discover its tools on the next step."
                  : <>Must be HTTPS. For on-demand APIs you can use {`{placeholders}`} that the AI will fill in (e.g. /api/listing/{`{id}`}).</>}
              </p>
            </div>

            {isCallableType(form.connection_type) && (
              <div className="space-y-2">
                <Label>HTTP method</Label>
                <Select value={form.http_method} onValueChange={(v) => setForm((f) => ({ ...f, http_method: v as "GET" | "POST" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {(form.connection_type === "url_feed" || isMcp(form.connection_type)) && (
              <div className="space-y-2">
                <Label>Refresh interval</Label>
                <Select
                  value={String(form.refresh_interval_seconds)}
                  onValueChange={(v) => setForm((f) => ({ ...f, refresh_interval_seconds: Number(v) }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">1 minute</SelectItem>
                    <SelectItem value="300">5 minutes (recommended)</SelectItem>
                    <SelectItem value="900">15 minutes</SelectItem>
                    <SelectItem value="3600">1 hour</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {isMcp(form.connection_type)
                    ? "How often we re-check the server for changes to its tool list in the background."
                    : "How often we refresh the cached data in the background."}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Authentication</Label>
              <Select
                value={form.auth_type}
                onValueChange={(v) => setForm((f) => ({ ...f, auth_type: v as AuthType, auth_creds: {} }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (public)</SelectItem>
                  <SelectItem value="bearer">Bearer token</SelectItem>
                  <SelectItem value="header">Custom header</SelectItem>
                  <SelectItem value="basic">Basic auth (username + password)</SelectItem>
                  <SelectItem value="query">API key in query string</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.auth_type === "bearer" && (
              <div className="space-y-2">
                <Label htmlFor="auth-token">Token</Label>
                <Input
                  id="auth-token"
                  type="password"
                  placeholder={editing ? "Leave blank to keep existing" : ""}
                  value={form.auth_creds.token ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, token: e.target.value } }))}
                />
                <p className="text-xs text-muted-foreground">Sent as <code>Authorization: Bearer ...</code>.</p>
              </div>
            )}
            {form.auth_type === "header" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="auth-header">Header name</Label>
                  <Input id="auth-header" placeholder="X-API-Key" value={form.auth_creds.header ?? ""} onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, header: e.target.value } }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-value">Value</Label>
                  <Input id="auth-value" type="password" placeholder={editing ? "Leave blank to keep" : ""} value={form.auth_creds.value ?? ""} onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, value: e.target.value } }))} />
                </div>
              </div>
            )}
            {form.auth_type === "basic" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="auth-user">Username</Label>
                  <Input id="auth-user" value={form.auth_creds.user ?? ""} onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, user: e.target.value } }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-pass">Password</Label>
                  <Input id="auth-pass" type="password" placeholder={editing ? "Leave blank to keep" : ""} value={form.auth_creds.pass ?? ""} onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, pass: e.target.value } }))} />
                </div>
              </div>
            )}
            {form.auth_type === "query" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="auth-param">Query param</Label>
                  <Input id="auth-param" placeholder="api_key" value={form.auth_creds.param ?? ""} onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, param: e.target.value } }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-pvalue">Value</Label>
                  <Input id="auth-pvalue" type="password" placeholder={editing ? "Leave blank to keep" : ""} value={form.auth_creds.value ?? ""} onChange={(e) => setForm((f) => ({ ...f, auth_creds: { ...f.auth_creds, value: e.target.value } }))} />
                </div>
              </div>
            )}

            {isCallableType(form.connection_type) && (
              <div className="space-y-2 pt-2 border-t">
                <Label>Tool parameters (what the AI passes)</Label>
                <p className="text-xs text-muted-foreground">
                  Define the arguments the AI fills when calling your endpoint. Use {`{name}`} in the URL or body to inject them.
                </p>
                <div className="space-y-2">
                  {form.args.map((a, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                      <Input
                        className="col-span-3"
                        placeholder="param_name"
                        value={a.name}
                        onChange={(e) => setForm((f) => {
                          const next = [...f.args]; next[idx] = { ...next[idx], name: e.target.value }; return { ...f, args: next };
                        })}
                      />
                      <Select
                        value={a.type}
                        onValueChange={(v) => setForm((f) => {
                          const next = [...f.args]; next[idx] = { ...next[idx], type: v as ArgField["type"] }; return { ...f, args: next };
                        })}
                      >
                        <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="STRING">string</SelectItem>
                          <SelectItem value="INTEGER">integer</SelectItem>
                          <SelectItem value="NUMBER">number</SelectItem>
                          <SelectItem value="BOOLEAN">boolean</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        className="col-span-6"
                        placeholder="What is this for? (AI sees this)"
                        value={a.description}
                        onChange={(e) => setForm((f) => {
                          const next = [...f.args]; next[idx] = { ...next[idx], description: e.target.value }; return { ...f, args: next };
                        })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="col-span-1 h-9 w-9"
                        onClick={() => setForm((f) => ({ ...f, args: f.args.filter((_, i) => i !== idx) }))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setForm((f) => ({ ...f, args: [...f.args, { name: "", type: "STRING", description: "", required: true }] }))}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add parameter
                  </Button>
                </div>
              </div>
            )}

            {isCallableType(form.connection_type) && form.http_method === "POST" && (
              <div className="space-y-2">
                <Label htmlFor="body-template">Request body template (JSON)</Label>
                <Textarea
                  id="body-template"
                  className="font-mono text-xs"
                  rows={4}
                  placeholder={`{ "listing_id": "{listing_id}", "date": "{date}" }`}
                  value={form.request_body_template_text}
                  onChange={(e) => setForm((f) => ({ ...f, request_body_template_text: e.target.value }))}
                />
              </div>
            )}

            {isGatedType(form.connection_type) && (
              <div className="space-y-4 pt-2 border-t">
                <div className="space-y-2">
                  <Label>{isMcp(form.connection_type) ? "When should the AI run this server's action tools?" : "When should the AI run this?"}</Label>
                  <Select
                    value={form.action_mode}
                    onValueChange={(v) => setForm((f) => ({ ...f, action_mode: v as FormState["action_mode"] }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ask">Ask me first — approve each time (recommended)</SelectItem>
                      <SelectItem value="auto">Automatically — no approval</SelectItem>
                      <SelectItem value="off">Off — don't offer {isMcp(form.connection_type) ? "this server's tools" : "this action"}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isMcp(form.connection_type) && (
                      <span className="block mb-1">Read-only lookups always run without approval. This setting controls the server's <span className="font-medium">action</span> tools.</span>
                    )}
                    {form.action_mode === "auto"
                      ? "The AI performs these on its own the moment a customer asks. Use only for low-risk actions."
                      : form.action_mode === "off"
                        ? (isMcp(form.connection_type) ? "The AI won't be offered any of this server's tools at all." : "The AI won't be offered this action at all.")
                        : "Each call waits in your Action Center for your approval before it runs."}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="success-message">Confirmation message to the customer (optional)</Label>
                  <Textarea
                    id="success-message"
                    rows={2}
                    placeholder="e.g. All done! Your order has been placed and we'll be in touch shortly."
                    value={form.success_message}
                    onChange={(e) => setForm((f) => ({ ...f, success_message: e.target.value }))}
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground">
                    Sent to the customer once the action has run (this is how an approved action confirms back to them). Use {`{param}`} to include a value they gave. Leave blank for a generic confirmation.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: test */}
        {step === "test" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {isMcp(form.connection_type)
                ? "We'll connect to your MCP server and list the tools it exposes — a safe, read-only check."
                : <>We'll call your endpoint now with the credentials you provided. {isCallableType(form.connection_type) ? "Parameters are sent empty for this test — make sure your endpoint handles that gracefully, or test with sample values via curl first." : ""}</>}
            </p>
            {form.connection_type === "rest_action" && (
              <Alert>
                <AlertDescription className="text-xs">
                  ⚠️ This is an <span className="font-medium">action</span> endpoint — running the test will actually perform it (with empty parameters). If that's not safe, skip the test and just save; you can verify it live later.
                </AlertDescription>
              </Alert>
            )}
            <Button onClick={handleTest} disabled={testing} className="w-full">
              {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {testing ? (isMcp(form.connection_type) ? "Connecting..." : "Testing...") : (isMcp(form.connection_type) ? "Discover tools" : "Run test")}
            </Button>
            {isMcp(form.connection_type)
              ? (mcpTestResult && <McpResultPreview result={mcpTestResult} />)
              : (testResult && <ResultPreview result={testResult} />)}
          </div>
        )}

        {/* Step 5: confirm */}
        {step === "confirm" && (
          <div className="space-y-3 text-sm">
            <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{form.name}</span>
                <Badge variant="outline" className="text-[10px]">
                  {form.connection_type === "url_feed" ? "Live feed" : form.connection_type === "rest_action" ? "Action" : isMcp(form.connection_type) ? "MCP server" : "On-demand API"}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">{form.description}</div>
              <div className="text-xs font-mono break-all">{isMcp(form.connection_type) ? form.endpoint_url : `${form.http_method} ${form.endpoint_url}`}</div>
              <div className="text-xs">Auth: {form.auth_type}</div>
              {(form.connection_type === "url_feed" || isMcp(form.connection_type)) && (
                <div className="text-xs">Refresh: every {form.refresh_interval_seconds}s</div>
              )}
              {isMcp(form.connection_type) && mcpTestResult?.ok && (
                <div className="text-xs">Tools discovered: {mcpTestResult.tools?.length ?? 0}</div>
              )}
              {isCallableType(form.connection_type) && form.args.length > 0 && (
                <div className="text-xs">Parameters: {form.args.map((a) => a.name).filter(Boolean).join(", ")}</div>
              )}
              {isGatedType(form.connection_type) && (
                <div className="text-xs">{isMcp(form.connection_type) ? "Action tools run: " : "Runs: "}{form.action_mode === "auto" ? "automatically" : form.action_mode === "off" ? "off (not offered to the AI)" : "with your approval"}</div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {isMcp(form.connection_type)
                ? "Once saved, the AI can use this server's tools when a customer asks. Lookups run on demand; action tools wait for your approval in the Action Center unless you chose otherwise."
                : form.connection_type === "rest_action"
                ? "Once saved, the AI can perform this action when a customer asks. For safety, each call waits for your approval in the Action Center before it actually runs."
                : "Once saved, the AI will start using this data on the next customer message."}
            </p>
          </div>
        )}

        <DialogFooter className="flex sm:justify-between items-center">
          <Button variant="ghost" disabled={!canBack || isSaving} onClick={goBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            {step !== "confirm" ? (
              <Button onClick={goNext} disabled={nextDisabled}>
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? "Save changes" : "Connect"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
